import { registerCodes } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

// src/error-codes.ts
var WEB_PORTAL_LIB_ID = "@norkron/web-portal";
try {
  registerCodes(WEB_PORTAL_LIB_ID, {
    WEB_PORTAL_TARGET_MISSING: {
      description: "selfCheck: a `<Portal>` references a `target` (id or selector) that does not resolve to a DOM node at render time. The portal content will fall back to `document.body` or fail to mount. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-portal/WEB_PORTAL_TARGET_MISSING",
      exampleFix: "Ensure the `target` element exists in the DOM before the portal mounts (render the host node earlier in the tree, or pass a ref-resolved element)."
    },
    WEB_PORTAL_Z_INDEX_NOT_ISOLATED: {
      description: "selfCheck: a portal-mounted subtree renders with no `isolation: isolate` (or sibling stacking-context) on an ancestor \u2014 a sibling overlay may render above the portal regardless of z-index. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-portal/WEB_PORTAL_Z_INDEX_NOT_ISOLATED",
      exampleFix: "Add `isolation: isolate` (or `transform: translateZ(0)` / `position: relative; z-index: 0`) on the portal root so its stacking context is independent from siblings."
    },
    WEB_PORTAL_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-portal/WEB_PORTAL_SELFCHECK_INTERNAL_ERROR",
      exampleFix: "File an issue against @norkron/web-portal with the finding.message stack context."
    }
  });
} catch {
}

// src/self-check.ts
var LIB_ID = "@norkron/web-portal";
var DEFAULT_ROOT_ID = "norkron-portal-root";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  return id ? `${tag}${id}` : tag;
}
function selfCheck(root, opts) {
  const safeOpts = {
    budgetMs: opts?.budgetMs ?? 50,
    minSeverity: opts?.minSeverity ?? "info",
    locale: opts?.locale ?? "en"
  };
  const findings = [];
  const ownerDoc = root.ownerDocument;
  try {
    const orphans = Array.from(root.querySelectorAll("[data-portal-orphan]"));
    for (const orphan of orphans) {
      const wantedId = orphan.getAttribute("data-portal-orphan");
      findings.push({
        libId: LIB_ID,
        code: "WEB_PORTAL_TARGET_MISSING",
        category: "flow",
        severity: "major",
        message: `Portal references rootId "${wantedId ?? "(unknown)"}" that does not exist in the document.`,
        selector: selectorFor(orphan),
        suggestedFix: "Mount the named portal host before the consumer (or omit `rootId` to use the auto-created `#norkron-portal-root`).",
        extras: { wantedRootId: wantedId }
      });
    }
    const targets = Array.from(root.querySelectorAll("[data-portal-target]"));
    for (const t of targets) {
      const id = t.getAttribute("data-portal-target");
      if (!id) continue;
      const found = ownerDoc?.getElementById(id);
      if (!found) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_PORTAL_TARGET_MISSING",
          category: "flow",
          severity: "major",
          message: `Portal target rootId "${id}" is declared but no matching element exists in the document.`,
          selector: selectorFor(t),
          suggestedFix: 'Render a container with id="' + id + '" or remove the explicit rootId to use the default.',
          extras: { wantedRootId: id }
        });
      }
    }
    const defaultRoot = ownerDoc?.getElementById(DEFAULT_ROOT_ID);
    if (defaultRoot && typeof window !== "undefined" && typeof window.getComputedStyle === "function") {
      const style = window.getComputedStyle(defaultRoot);
      const position = style.position;
      const zIndex = style.zIndex;
      const isolated = position && position !== "static" || zIndex && zIndex !== "auto" && zIndex !== "";
      if (!isolated) {
        findings.push({
          libId: LIB_ID,
          code: "WEB_PORTAL_Z_INDEX_NOT_ISOLATED",
          category: "layout",
          severity: "major",
          message: "Default portal root #norkron-portal-root has no stacking context (position: static AND z-index: auto). Portaled content may render under fixed page chrome.",
          selector: `#${DEFAULT_ROOT_ID}`,
          suggestedFix: "Style #norkron-portal-root with `position: fixed; inset: 0; z-index: 1000; pointer-events: none;` (children re-enable pointer events).",
          extras: { position, zIndex }
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_PORTAL_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var DEFAULT_ROOT_ID2 = "norkron-portal-root";
function Portal({ container, rootId, children }) {
  const [mountNode, setMountNode] = useState(null);
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (container) {
      setMountNode(container);
      return;
    }
    const id = rootId || DEFAULT_ROOT_ID2;
    let node = document.getElementById(id);
    let created = false;
    if (!node) {
      node = document.createElement("div");
      node.id = id;
      document.body.appendChild(node);
      created = true;
    }
    setMountNode(node);
    return () => {
      if (created && node && node.childNodes.length === 0 && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    };
  }, [container, rootId]);
  if (!mountNode) return null;
  return createPortal(children, mountNode);
}

// src/scroll-lock.ts
var lockCount = 0;
var savedOverflow = null;
var savedPaddingRight = null;
function lockScroll() {
  if (typeof document === "undefined") {
    return () => {
    };
  }
  lockCount += 1;
  if (lockCount === 1) {
    const body = document.body;
    const docEl = document.documentElement;
    savedOverflow = body.style.overflow;
    savedPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - docEl.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPx = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPx + scrollbarWidth}px`;
    }
    body.style.overflow = "hidden";
  }
  let released = false;
  return function release() {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      const body = document.body;
      body.style.overflow = savedOverflow ?? "";
      body.style.paddingRight = savedPaddingRight ?? "";
      savedOverflow = null;
      savedPaddingRight = null;
    }
  };
}
function __getScrollLockCount() {
  return lockCount;
}
function useScrollLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const release = lockScroll();
    return () => {
      release();
    };
  }, [enabled]);
}

// src/index.ts
registerLazy(
  "@norkron/web-portal",
  () => selfCheck
);

export { Portal, WEB_PORTAL_LIB_ID, __getScrollLockCount, lockScroll, selfCheck, useScrollLock };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map